import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Clock, LoaderCircle, Package, Truck, XCircle } from 'lucide-react';

import { ApiError } from '@/api/client';
import { patientApi } from '@/api/patientClient';
import { PatientSessionProvider } from './patientSession';
import type { PatientOrder, PatientOrderStatus } from './types';

function money(n: number) {
  return `₹${n.toFixed(2)}`;
}

const STATUS_COPY: Record<PatientOrderStatus, { title: string; detail: string; icon: typeof CheckCircle2; tone: string }> = {
  PendingPayment: { title: 'Confirming your payment…', detail: 'This usually takes a few seconds.', icon: LoaderCircle, tone: 'text-muted-foreground' },
  Paid: { title: 'Payment received', detail: 'The pharmacy will review your order shortly.', icon: CheckCircle2, tone: 'text-status-ok' },
  Confirmed: { title: 'Order confirmed', detail: 'The pharmacy is preparing your order.', icon: Package, tone: 'text-status-ok' },
  Packed: { title: 'Order packed', detail: 'Ready to be sent out or picked up.', icon: Package, tone: 'text-status-ok' },
  OutForDelivery: { title: 'Out for delivery', detail: 'On its way to you.', icon: Truck, tone: 'text-status-ok' },
  ReadyForPickup: { title: 'Ready for pickup', detail: 'You can collect it from the store.', icon: Package, tone: 'text-status-ok' },
  Completed: { title: 'Order completed', detail: 'Thank you!', icon: CheckCircle2, tone: 'text-status-ok' },
  Rejected: { title: 'Order rejected', detail: 'The pharmacy could not fulfil this order — any payment has been refunded.', icon: XCircle, tone: 'text-status-out' },
  Cancelled: { title: 'Order cancelled', detail: 'Any payment has been refunded.', icon: XCircle, tone: 'text-status-out' },
  PaymentFailed: { title: 'Payment failed', detail: 'The payment did not go through. You have not been charged.', icon: XCircle, tone: 'text-status-out' },
};

export function StoreOrderStatusPage() {
  return (
    <PatientSessionProvider>
      <StoreOrderStatusInner />
    </PatientSessionProvider>
  );
}

function StoreOrderStatusInner() {
  const { orderId } = useParams();
  const [order, setOrder] = useState<PatientOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const result = await patientApi.get<PatientOrder>(`/api/patient-orders/${orderId}`);
        if (cancelled) return;
        setOrder(result);
        attempts.current += 1;
        // Keep polling only while payment is still settling, and only for a
        // bounded window — the webhook is the real source of truth and
        // usually lands within a couple of seconds, but this must not spin
        // forever if it's delayed.
        if (result.status === 'PendingPayment' && attempts.current < 20) {
          timer = setTimeout(poll, 3000);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.isUnauthorized
            ? 'Please log in to view this order.'
            : err instanceof ApiError
              ? err.message
              : 'Could not load this order.',
        );
      }
    };

    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orderId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const copy = STATUS_COPY[order.status];
  const Icon = copy.icon;

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-4 py-12 text-center">
      <Icon className={`h-14 w-14 ${copy.tone} ${order.status === 'PendingPayment' ? 'animate-spin' : ''}`} />
      <h1 className="mt-4 font-display text-2xl">{copy.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{copy.detail}</p>

      <div className="mt-8 w-full rounded-2xl border border-border/60 bg-card p-4 text-left">
        <div className="space-y-2">
          {order.items.map((item) => (
            <div key={item.medicineId} className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {item.medicineName} × {item.quantity}
              </span>
              <span>{money(item.total)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-1.5 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {order.deliveryMethod === 'Delivery' ? `Delivery to ${order.deliveryAddress}` : 'Store pickup'}
        </div>
        <div className="mt-2 flex justify-between text-base font-semibold">
          <span>Total</span>
          <span>{money(order.total)}</span>
        </div>
      </div>
    </div>
  );
}
